import {
  GetUserShotlistColumnDirection,
  GetUserShotlistColumnFavorites,
  GetUserShotlistColumnOrder,
  SetUserShotlistColumnDirection,
  SetUserShotlistColumnFavorites,
  SetUserShotlistColumnOrder
} from "../isdlData/matrixUserData";

export const USER_PREFERENCES_IMPORTED_EVENT = "lmreceiver-user-preferences-imported";
export const SHOW_DETAILED_SUMMARY_STORAGE_KEY = "showDetailedSummary";
export const SHOTLIST_COLUMNS_ORDER_EVENT = "shotlist-columns-order-updated";
export const SHOTLIST_FAVORITES_EVENT = "shotlist-favorites-updated";

const USER_PREFERENCES_FILE_NAME = "lmreceiver-preferences.json";
const USER_PREFERENCES_VERSION = 1;
const LOCAL_STORAGE_KEYS = [
  "theme",
  "savedMainWidgets",
  "favoriteWidgets",
  "savedGolfer",
  "savedVenue",
  "threeDCameraState",
  "lmreceiver_template_selection",
  SHOW_DETAILED_SUMMARY_STORAGE_KEY,
  "desktop_leftPanelState",
  "desktop_leftTabSelection",
  "desktop_rightPanelState"
];
const LOCAL_STORAGE_PREFIXES = [
  "shotListColumnsOrder_"
];

function cloneJsonSafe(value) {
  if (value === undefined) {
    return undefined;
  }

  return JSON.parse(JSON.stringify(value));
}

function getManagedLocalStorageKeys() {
  const matchedKeys = new Set(LOCAL_STORAGE_KEYS);

  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (!key) {
      continue;
    }

    if (LOCAL_STORAGE_PREFIXES.some((prefix) => key.startsWith(prefix))) {
      matchedKeys.add(key);
    }
  }

  return Array.from(matchedKeys);
}

export function getShowDetailedSummaryPreference() {
  return localStorage.getItem(SHOW_DETAILED_SUMMARY_STORAGE_KEY) === "true";
}

export function setShowDetailedSummaryPreference(value) {
  localStorage.setItem(SHOW_DETAILED_SUMMARY_STORAGE_KEY, value ? "true" : "false");
}

function buildPreferencesPayload({ localStorageData, shotListSettings }) {
  return {
    app: "lmreceiver",
    version: USER_PREFERENCES_VERSION,
    exportedAt: new Date().toISOString(),
    preferences: {
      localStorage: localStorageData,
      shotListSettings
    }
  };
}

async function collectPreferences() {
  const localStorageData = {};
  getManagedLocalStorageKeys().forEach((key) => {
    const value = localStorage.getItem(key);
    if (value !== null) {
      localStorageData[key] = value;
    }
  });

  const shotListSettings = {
    favorites: cloneJsonSafe(await GetUserShotlistColumnFavorites()),
    order: cloneJsonSafe(await GetUserShotlistColumnOrder()),
    direction: cloneJsonSafe(await GetUserShotlistColumnDirection())
  };

  return buildPreferencesPayload({ localStorageData, shotListSettings });
}

function downloadTextFile(text, fileName) {
  const blob = new Blob([text], { type: "application/json" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
}

async function saveTextToFile(text, fileName) {
  if (window.showSaveFilePicker) {
    try {
      const handle = await window.showSaveFilePicker({
        suggestedName: fileName,
        types: [
          {
            description: "JSON Files",
            accept: {
              "application/json": [".json"]
            }
          }
        ]
      });
      const writable = await handle.createWritable();
      await writable.write(text);
      await writable.close();
      return true;
    } catch (error) {
      if (error?.name === "AbortError") {
        return false;
      }
      console.warn("Falling back to browser download for preferences export.", error);
    }
  }

  downloadTextFile(text, fileName);
  return true;
}

async function loadTextFromFile() {
  if (window.showOpenFilePicker) {
    try {
      const [handle] = await window.showOpenFilePicker({
        types: [
          {
            description: "JSON Files",
            accept: {
              "application/json": [".json"]
            }
          }
        ],
        multiple: false
      });
      const file = await handle.getFile();
      return await file.text();
    } catch (error) {
      if (error?.name === "AbortError") {
        return null;
      }
      console.warn("Falling back to file input for preferences import.", error);
    }
  }

  return await new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".json,application/json";
    input.style.display = "none";

    input.onchange = async (event) => {
      const file = event.target.files?.[0];
      document.body.removeChild(input);

      if (!file) {
        resolve(null);
        return;
      }

      resolve(await file.text());
    };

    input.oncancel = () => {
      document.body.removeChild(input);
      resolve(null);
    };

    document.body.appendChild(input);
    input.click();
  });
}

function validateImportedPreferences(parsed) {
  if (!parsed || typeof parsed !== "object") {
    throw new Error("Preferences file is empty or invalid.");
  }

  if (parsed.app !== "lmreceiver") {
    throw new Error("Preferences file is not for LMReceiver.");
  }

  const localStorageData = parsed?.preferences?.localStorage;
  const shotListSettings = parsed?.preferences?.shotListSettings;

  if (!localStorageData || typeof localStorageData !== "object" || Array.isArray(localStorageData)) {
    throw new Error("Preferences file is missing localStorage settings.");
  }

  if (!shotListSettings || typeof shotListSettings !== "object" || Array.isArray(shotListSettings)) {
    throw new Error("Preferences file is missing shot list settings.");
  }

  return {
    localStorageData,
    shotListSettings
  };
}

function applyLocalStoragePreferences(localStorageData) {
  getManagedLocalStorageKeys().forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(localStorageData, key)) {
      const nextValue = localStorageData[key];
      if (typeof nextValue === "string") {
        localStorage.setItem(key, nextValue);
      } else if (nextValue === null) {
        localStorage.removeItem(key);
      }
    } else {
      localStorage.removeItem(key);
    }
  });

  Object.keys(localStorageData).forEach((key) => {
    if (
      (LOCAL_STORAGE_KEYS.includes(key) || LOCAL_STORAGE_PREFIXES.some((prefix) => key.startsWith(prefix)))
      && typeof localStorageData[key] === "string"
    ) {
      localStorage.setItem(key, localStorageData[key]);
    }
  });
}

async function applyShotListSettings(shotListSettings) {
  await SetUserShotlistColumnFavorites(shotListSettings.favorites ?? null);
  await SetUserShotlistColumnOrder(shotListSettings.order ?? null);
  await SetUserShotlistColumnDirection(shotListSettings.direction ?? null);
  window.dispatchEvent(new CustomEvent(SHOTLIST_FAVORITES_EVENT));
  window.dispatchEvent(new CustomEvent(SHOTLIST_COLUMNS_ORDER_EVENT));
}

export async function exportUserPreferences() {
  const payload = await collectPreferences();
  const text = JSON.stringify(payload, null, 2);
  return await saveTextToFile(text, USER_PREFERENCES_FILE_NAME);
}

export async function importUserPreferences() {
  const text = await loadTextFromFile();
  if (!text) {
    return { imported: false };
  }

  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch (error) {
    throw new Error("Preferences file is not valid JSON.");
  }

  const { localStorageData, shotListSettings } = validateImportedPreferences(parsed);

  applyLocalStoragePreferences(localStorageData);
  await applyShotListSettings(shotListSettings);

  const importedPayload = buildPreferencesPayload({
    localStorageData,
    shotListSettings
  });
  window.dispatchEvent(new CustomEvent(USER_PREFERENCES_IMPORTED_EVENT, { detail: importedPayload }));

  return { imported: true, payload: importedPayload };
}
